import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  useRecordConsent,
  type ConsentGivenByKind,
  type ConsentIdentityVerification,
  type ConsentTextVersionRow,
} from '@/hooks/useConsentRecords';
import {
  CONSENT_TYPES,
  STAFF_RECORD_METHODS,
  consentScopeChannel,
  consentTypeGroup,
  consentTypeIsRefusal,
  consentTypeRequiresExpiry,
  consentTypeUsesRecipient,
  dateInputToExpiryIso,
  effectiveConsentTextVersion,
  oneYearFromDateInput,
  sortConsentTextVersions,
  validateConsentExpiry,
  type ConsentRecordMethod,
  type ConsentTypeGroup,
  type ConsentTypeKey,
} from '@/lib/consent/consentTypes';

const IDENTITY_METHODS: ConsentIdentityVerification[] = ['known_to_staff', 'id_document', 'mitid', 'other'];
const GIVEN_BY_KINDS: ConsentGivenByKind[] = ['patient', 'custody_holder', 'guardian', 'future_attorney'];
const GROUP_ORDER: ConsentTypeGroup[] = ['information', 'marketing', 'health_law', 'refusal', 'treatment'];

type StaffDecision = 'given' | 'refused';

export interface RegisterConsentSlideOverProps {
  open: boolean;
  onClose: () => void;
  clinicId: string;
  patientId: string;
  textVersions: ConsentTextVersionRow[];
  initialType?: ConsentTypeKey | null;
}

export function RegisterConsentSlideOver({
  open,
  onClose,
  clinicId,
  patientId,
  textVersions,
  initialType,
}: RegisterConsentSlideOverProps) {
  const { t } = useTranslation();
  const record = useRecordConsent();

  const [type, setType] = useState<ConsentTypeKey>(initialType ?? 'privacy_notice');
  const [textVersionId, setTextVersionId] = useState('');
  const [decision, setDecision] = useState<StaffDecision>('given');
  const [method, setMethod] = useState<ConsentRecordMethod>('oral');
  const [identity, setIdentity] = useState<ConsentIdentityVerification>('known_to_staff');
  const [givenByKind, setGivenByKind] = useState<ConsentGivenByKind>('patient');
  const [givenByName, setGivenByName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [purpose, setPurpose] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [note, setNote] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const versionsForType = useMemo(
    () => sortConsentTextVersions(textVersions.filter((v) => v.consent_type === type)),
    [textVersions, type],
  );

  useEffect(() => {
    if (!open) return;
    const nextType = initialType ?? 'privacy_notice';
    setType(nextType);
    setDecision('given');
    setMethod('oral');
    setIdentity('known_to_staff');
    setGivenByKind('patient');
    setGivenByName('');
    setRecipient('');
    setPurpose('');
    setNote('');
    setSubmitted(false);
    setExpiresOn(consentTypeRequiresExpiry(nextType) ? oneYearFromDateInput(new Date()) : '');
  }, [open, initialType]);

  useEffect(() => {
    const effective = effectiveConsentTextVersion(textVersions, clinicId, type);
    setTextVersionId(effective?.id ?? versionsForType[0]?.id ?? '');
  }, [type, textVersions, clinicId, versionsForType]);

  const changeType = (next: ConsentTypeKey) => {
    setType(next);
    setDecision('given');
    setExpiresOn(consentTypeRequiresExpiry(next) ? oneYearFromDateInput(new Date()) : '');
  };

  const isRefusal = consentTypeIsRefusal(type);
  const usesRecipient = consentTypeUsesRecipient(type);
  const expiresAtIso = expiresOn ? dateInputToExpiryIso(expiresOn) : null;
  const expiryState = validateConsentExpiry(type, decision === 'given', expiresAtIso);

  const errors = {
    text: !textVersionId ? t('consent.register.errors.text_required') : undefined,
    recipient:
      usesRecipient && !recipient.trim() && !purpose.trim()
        ? t('consent.register.errors.recipient_required')
        : undefined,
    givenByName:
      givenByKind !== 'patient' && !givenByName.trim()
        ? t('consent.register.errors.given_by_name_required')
        : undefined,
    expiry: expiryState === 'ok' ? undefined : t(`consent.register.errors.expiry_${expiryState}`),
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const dirty = note.length > 0 || recipient.length > 0 || purpose.length > 0 || givenByName.length > 0;

  const handleSave = async () => {
    setSubmitted(true);
    if (hasErrors) return;
    try {
      await record.mutateAsync({
        p_patient_id: patientId,
        p_consent_type: type,
        p_decision: decision,
        p_text_version_id: textVersionId,
        p_method: method,
        p_identity_verification_method: identity,
        p_given_by_kind: givenByKind,
        ...(givenByKind !== 'patient' ? { p_given_by_name: givenByName.trim() } : {}),
        ...(usesRecipient && recipient.trim() ? { p_scope_recipient_name: recipient.trim() } : {}),
        ...(usesRecipient && purpose.trim() ? { p_scope_purpose: purpose.trim() } : {}),
        ...(consentScopeChannel(type) ? { p_scope_channel: consentScopeChannel(type) as string } : {}),
        ...(expiresAtIso && decision === 'given' && !isRefusal ? { p_expires_at: expiresAtIso } : {}),
        ...(note.trim() ? { p_note: note.trim() } : {}),
      });
      toast.success(t('consent.register.saved'));
      onClose();
    } catch (err) {
      console.error('record_consent failed', err);
      toast.error(t('consent.register.save_error'));
    }
  };

  const selectedVersion = versionsForType.find((v) => v.id === textVersionId) ?? null;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t('consent.register.title')}
      description={t('consent.register.description')}
      dirty={dirty}
      wide
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button onClick={() => void handleSave()} loading={record.isPending}>
              {t('consent.register.save')}
            </Button>
          }
        />
      }
    >
      <div className="space-y-s4">
        <Select
          id="consent-register-type"
          label={t('consent.fields.type')}
          value={type}
          onChange={(e) => changeType(e.target.value as ConsentTypeKey)}
        >
          {GROUP_ORDER.map((group) => (
            <optgroup key={group} label={t(`consent.groups.${group}`)}>
              {CONSENT_TYPES.filter((ct) => consentTypeGroup(ct) === group).map((ct) => (
                <option key={ct} value={ct}>
                  {t(`consent.types.${ct}`)}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>

        {versionsForType.length === 0 ? (
          <Banner tone="warning">{t('consent.register.no_text_versions')}</Banner>
        ) : (
          <div className="space-y-s2">
            <Select
              id="consent-register-version"
              label={t('consent.fields.text_version')}
              value={textVersionId}
              onChange={(e) => setTextVersionId(e.target.value)}
              error={submitted ? errors.text : undefined}
            >
              {versionsForType.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.version_label} · {v.clinic_id ? t('consent.clinic_text') : t('consent.clinio_default')}
                </option>
              ))}
            </Select>
            {selectedVersion ? (
              <details className="rounded-ctl border border-ds-line bg-ds-bg p-s3">
                <summary className={cn(TYPE.small, 'cursor-pointer font-medium text-ds-teal')}>
                  {t('consent.show_text')}
                </summary>
                <p className={cn(TYPE.small, 'mt-s2 whitespace-pre-wrap text-ds-text')}>{selectedVersion.body}</p>
              </details>
            ) : null}
          </div>
        )}

        <Select
          id="consent-register-decision"
          label={t('consent.fields.decision')}
          value={decision}
          onChange={(e) => setDecision(e.target.value as StaffDecision)}
        >
          {isRefusal ? (
            <option value="given">{t('consent.decision.refusal_given')}</option>
          ) : (
            <>
              <option value="given">{t('consent.register.decision_given')}</option>
              <option value="refused">{t('consent.register.decision_refused')}</option>
            </>
          )}
        </Select>

        {usesRecipient ? (
          <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
            <Input
              id="consent-register-recipient"
              label={t('consent.fields.recipient')}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              error={submitted ? errors.recipient : undefined}
            />
            <Input
              id="consent-register-purpose"
              label={t('consent.fields.purpose')}
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </div>
        ) : null}

        {decision === 'given' && !isRefusal && (consentTypeRequiresExpiry(type) || usesRecipient) ? (
          <div className="space-y-s1">
            <DanishDateField
              label={t('consent.fields.expires_at')}
              value={expiresOn}
              onChange={setExpiresOn}
              required={consentTypeRequiresExpiry(type)}
              calendar
            />
            {consentTypeRequiresExpiry(type) ? (
              <p className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.register.one_year_hint')}</p>
            ) : null}
            {submitted && errors.expiry ? (
              <p className={cn(TYPE.small, 'text-ds-red-ink')} role="alert">
                {errors.expiry}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
          <Select
            id="consent-register-method"
            label={t('consent.fields.method')}
            value={method}
            onChange={(e) => setMethod(e.target.value as ConsentRecordMethod)}
          >
            {STAFF_RECORD_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`consent.method.${m}`)}
              </option>
            ))}
            <option value="portal" disabled>
              {t('consent.method.portal')} – {t('consent.register.portal_only_by_patient')}
            </option>
          </Select>
          <Select
            id="consent-register-identity"
            label={t('consent.fields.identity')}
            value={identity}
            onChange={(e) => setIdentity(e.target.value as ConsentIdentityVerification)}
          >
            {IDENTITY_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`consent.identity.${m}`)}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
          <Select
            id="consent-register-given-by"
            label={t('consent.fields.given_by')}
            value={givenByKind}
            onChange={(e) => setGivenByKind(e.target.value as ConsentGivenByKind)}
          >
            {GIVEN_BY_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`consent.given_by.${k}`)}
              </option>
            ))}
          </Select>
          {givenByKind !== 'patient' ? (
            <Input
              id="consent-register-given-by-name"
              label={t('consent.fields.given_by_name')}
              value={givenByName}
              onChange={(e) => setGivenByName(e.target.value)}
              error={submitted ? errors.givenByName : undefined}
            />
          ) : null}
        </div>

        <Textarea
          id="consent-register-note"
          label={`${t('consent.fields.note')} (${t('common.optional')})`}
          rows={3}
          maxLength={4000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </SlideOver>
  );
}
